import React from 'react';
import { Scroll, History, MapPin, Phone, Users, Calendar } from 'lucide-react';
import Navbar from '../components/Home/Navbar';
import Footer from '../components/Home/Footer'; // Added Footer Import

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-orange-50 font-sans text-stone-900">
      <Navbar />
      
      {/* Header Section */}
      <section className="bg-orange-600 text-white py-16 px-4 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none flex justify-center items-center">
          <span className="text-[20rem] font-serif">ॐ</span>
        </div>
        <div className="relative z-10">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">Devasthanam History & Inscriptions</h1>
          <p className="text-orange-100 text-lg max-w-2xl mx-auto">
            Exploring the 11th-century legacy of the Western Chalukya Dynasty in Chandippa.
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 mt-12 mb-20">
        {/* Simplified Grid: Shows both languages side-by-side permanently */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* Telugu Section */}
          <div className="space-y-8 bg-white p-8 md:p-12 rounded-[2rem] shadow-xl border-t-4 border-orange-500">
            <div className="flex items-center gap-3 text-orange-600 mb-6">
              <Scroll size={28} />
              <h2 className="text-2xl font-bold font-serif">మరకత శివలింగం ఆలయ చరిత్ర</h2>
            </div>
            
            <div className="prose prose-stone max-w-none text-stone-700 leading-relaxed space-y-6">
              <p className="font-bold text-lg text-orange-900 bg-orange-50 p-4 rounded-xl border-l-4 border-orange-600">
                గ్రా. చందిప్ప, మం. శంకర్ పల్లి, రంగారెడ్డి జిల్లా.
              </p>
              
              <p>
                ఈ శాసనము పశ్చిమ చాళుక్య వంశజులలో సుప్రసిద్ధ చక్రవర్తి శ్రీ త్రిభువనమల్ల బిరుదాంకితుడైన ఆరవ విక్రమాదిత్యుడు, కళ్యాణపురము మహా రాజధానిగా చేసుకొని సుఖ:సంకథా వినోదముగా రాజ్యము చేయుచుండగా, (పరిపాలనా కాలం కీ.శ. 1076 నుండి 1126 వరకు)
              </p>

              <p>
                శ్రీ త్రిభువన మల్లదేవ మహారాజుల వారు తన అతి విశాల భూభాగ సామ్రాజ్యమునందు నిత్యభూదాన, యజ్ఞ వశాత్ చందిప్ప గ్రామమును, వేద పారంగతులైన భూసురులకు (బ్రాహ్మణులకు) అగ్రహారముగా ప్రకటించి ఈ గ్రామ సీమయందు గల సమస్త భూమిని అగ్రహారమునకు కేటాయించారు.
              </p>

              <div className="bg-stone-50 p-6 rounded-2xl border border-stone-100 space-y-4">
                <h3 className="font-bold text-orange-800">విరాళము వివరాలు:</h3>
                <ul className="list-disc ml-5 space-y-2 text-sm">
                  <li>ఆలయ నిర్వహణకు పశ్చిమ దిశలో హెబ్బిహాళ పొలములో 34 మత్తరు (153 ఎకరములు) భూమి.</li>
                  <li>దేవుడి నైవేద్యమునకు 2-20 గుంటల వరిపొలం నీరావరి భూమి.</li>
                  <li>పూజకు మరియు పూదోటకు దక్షిణ దిశలో ఒక మత్తరు (4-20 గుంటలు) భూమి.</li>
                  <li>నిత్య నందాదీపమునకు ఒక నువ్వుల గానుగ.</li>
                </ul>
              </div>

              <p>
                శ్రీ మచ్చాళుక్య విక్రమకాలపు 25 (కీ.శ. 1101వ సంవత్సరం) కార్తీక మాసమునందు శుక్ల పక్ష పంచమి (అక్టోబరు 23) రోజున సోమేశ్వర లింగ ప్రతిష్ట గావించబడింది.
              </p>
            </div>
          </div>

          {/* English Section */}
          <div className="space-y-8 bg-white p-8 md:p-12 rounded-[2rem] shadow-xl border-t-4 border-orange-600">
            <div className="flex items-center gap-3 text-orange-600 mb-6">
              <History size={28} />
              <h2 className="text-2xl font-bold font-serif">History of Marakata Shivalingam</h2>
            </div>

            <div className="prose prose-stone max-w-none text-stone-700 leading-relaxed space-y-6">
              <p className="font-bold text-lg text-orange-900 bg-orange-50 p-4 rounded-xl border-l-4 border-orange-600">
                Village: Chandippa, Shankarpally, Ranga Reddy District.
              </p>

              <p>
                This inscription records the reign of the illustrious Western Chalukya Emperor, <strong>Tribhuvanamalla Vikramaditya VI</strong>, who ruled from his capital Kalyanapuram (1076 AD – 1126 AD). 
              </p>

              <p>
                The Emperor declared Chandippa as an <em>Agrahara</em> (sacred land grant) to Vedic scholars and donated village lands to support the <strong>Someswara Devasthanam</strong>.
              </p>

              <div className="bg-stone-50 p-6 rounded-2xl border border-stone-100 space-y-4">
                <h3 className="font-bold text-orange-800">Details of the Grant:</h3>
                <ul className="list-disc ml-5 space-y-2 text-sm">
                  <li><strong>Maintenance:</strong> 34 Mattaru (approx. 153 acres) of black soil land named 'Hebbihala' on the western side.</li>
                  <li><strong>Offerings:</strong> 2 acres and 20 guntas of wetland for daily <em>Naivedyam</em>.</li>
                  <li><strong>Sacred Garden:</strong> 1 Mattaru (4.20 acres) for flowers and temple worship.</li>
                  <li><strong>Eternal Light:</strong> One sesame oil mill for the <em>Nanda Deepam</em>.</li>
                </ul>
              </div>

              <p>
                In the 25th year of the Vikrama era (<strong>1101 AD</strong>), on Kartika Shukla Panchami (Oct 23rd), the Someswara Shivalingam was installed by devotee Jakkanabbe.
              </p>
            </div>
          </div>
        </div>

        {/* Committee Section */}
        <section className="mt-16 bg-orange-900 text-white p-8 md:p-12 rounded-[3rem] shadow-2xl relative overflow-hidden">
          <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-2 text-orange-400 font-bold mb-4 uppercase tracking-[0.2em] text-sm">
                <Users size={20} /> Issued By
              </div>
              <h3 className="text-3xl font-serif font-bold mb-2">Devasthanam Committee</h3>
              <p className="text-orange-200 opacity-80 mb-6 italic">
                Village Elders & Residents of Chandippa Village.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-orange-800 rounded-full flex items-center justify-center">
                    <MapPin size={18} />
                  </div>
                  <span className="text-sm">Chandippa, Shankarpalli Mandal, Ranga Reddy Dist, Telangana, India-501203</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-orange-800 rounded-full flex items-center justify-center">
                    <Phone size={18} />
                  </div>
                  <span className="text-sm">76808 79388, 96669 59911</span>
                </div>
              </div>
            </div>
            <div className="hidden md:flex justify-end">
              <div className="w-64 h-64 border-4 border-orange-800 rounded-full flex items-center justify-center p-4">
                <div className="text-center">
                  <Calendar className="mx-auto mb-2 text-orange-500" size={40} />
                  <p className="text-xs uppercase tracking-widest text-orange-300">Inscribed in</p>
                  <p className="text-4xl font-serif font-bold">1101 AD</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer /> {/* Added Footer component here */}
    </div>
  );
};

export default AboutUs;